import { CloseOutlined, LockOutlined } from "@ant-design/icons";
import { Button, Form, Modal, Select, Space, Typography } from "antd";
import { useEffect } from "react";
import { useOrganization } from "@/modules/organizations/hooks/useOrganizations";
import { useCreateCrossAccess, useUpdateCrossAccess } from "../hooks/useCrossAccess";
import { useCrossAccessStore } from "../store/cross-access.store";
import type { CreateCrossAccessRequest, UpdateCrossAccessRequest } from "../types";
import { OrganizationTreeSelect } from "./OrganizationTreeSelect";

const { Text } = Typography;

const ACCESS_TYPE_LABELS: Record<string, string> = {
	READ: "Faqat o'qish",
	WRITE: "O'qish/Yozish",
};

export function CrossAccessFormModal() {
	const [form] = Form.useForm<CreateCrossAccessRequest>();
	const { modalOpen, editingItem, defaultViewerId, closeModal } = useCrossAccessStore();

	const { mutate: create, isPending: isCreating } = useCreateCrossAccess();
	const { mutate: update, isPending: isUpdating } = useUpdateCrossAccess();

	const viewerId = Form.useWatch("viewerId", form);
	const targetId = Form.useWatch("targetId", form);
	const accessType = Form.useWatch("accessType", form);

	const handleClose = () => {
		closeModal();
		form.resetFields();
	};

	const handleUpdate = (values: CreateCrossAccessRequest) => {
		if (!editingItem) {
			return;
		}
		const updateData: UpdateCrossAccessRequest = {};
		if (values.viewerId !== editingItem.viewer?.id) {
			updateData.viewerId = values.viewerId;
		}
		if (values.targetId !== editingItem.target?.id) {
			updateData.targetId = values.targetId;
		}
		if (values.accessType !== editingItem.accessType) {
			updateData.accessType = values.accessType;
		}
		update({ id: editingItem.id, data: updateData }, { onSuccess: handleClose });
	};

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			if (editingItem) {
				handleUpdate(values);
			} else {
				create(values, { onSuccess: handleClose });
			}
		});
	};

	useEffect(() => {
		if (editingItem) {
			form.setFieldsValue({
				viewerId: editingItem.viewer?.id,
				targetId: editingItem.target?.id,
				accessType: editingItem.accessType,
			});
		} else {
			form.resetFields();
			form.setFieldsValue({
				...(defaultViewerId && { viewerId: defaultViewerId }),
				accessType: "READ",
			});
		}
	}, [editingItem, defaultViewerId, form]);

	const { data: viewerOrg } = useOrganization(viewerId);
	const { data: targetOrg } = useOrganization(targetId);

	const accessDetails = {
		viewerName: viewerId ? (viewerOrg?.name ?? "-") : "-",
		targetName: targetId ? (targetOrg?.name ?? "-") : "-",
		accessType: accessType ? (ACCESS_TYPE_LABELS[accessType] ?? accessType) : "-",
	};

	return (
		<Modal
			open={modalOpen}
			onCancel={handleClose}
			destroyOnHidden
			width={720}
			footer={null}
			closeIcon={null}
			closable={false}
			styles={{ body: { padding: 0 } }}
			className="cross-access-modal"
		>
			{/* Header */}
			<div className="flex items-center justify-between border-b px-6 py-4">
				<Space>
					<LockOutlined className="text-ink-soft text-base" />
					<Text strong className="text-base">
						Tashkilotlararo kirish huquqlari
					</Text>
				</Space>
				<button
					type="button"
					onClick={handleClose}
					className="p-1 text-ink-soft hover:text-ink"
					aria-label="Yopish"
				>
					<CloseOutlined className="text-lg" />
				</button>
			</div>

			<div className="px-6 py-5">
				<div className="mb-6">
					<Text strong className="block mb-4 text-ink">
						Kirish sozlamalari
					</Text>
					<Form form={form} layout="vertical" requiredMark={false} onFinish={handleSubmit}>
						<Form.Item
							name="viewerId"
							label="Ko'ruvchi tashkilot"
							rules={[
								{ required: true, message: "Ko'ruvchi tashkilotni tanlang" },
								{
									validator: (_, val) => {
										if (val && targetId && val === targetId) {
											return Promise.reject(new Error("Ko'ruvchi tashkilot bir xil bo'lmasin"));
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<OrganizationTreeSelect placeholder="Tashkilotni tanlang" excludeId={targetId} />
						</Form.Item>

						<Form.Item
							name="targetId"
							label="Tashkilot"
							rules={[
								{ required: true, message: "Tashkilotni tanlang" },
								{
									validator: (_, val) => {
										if (val && viewerId && val === viewerId) {
											return Promise.reject(new Error("Ko'ruvchi va tashkilot bir xil bo'lmasin"));
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<OrganizationTreeSelect placeholder="Tashkilotni tanlang" excludeId={viewerId} />
						</Form.Item>

						<Form.Item
							name="accessType"
							label="Amal"
							rules={[{ required: true, message: "Kirish turini tanlang" }]}
						>
							<Select
								placeholder="Tanlang"
								disabled
								options={[
									{ value: "READ", label: "Faqat o'qish" },
									{ value: "WRITE", label: "O'qish/Yozish" },
								]}
							/>
						</Form.Item>

						<Form.Item className="mb-0">
							<Button
								type="primary"
								htmlType="submit"
								block
								loading={isCreating || isUpdating}
								id="cross-access-create-btn"
								className="h-10"
							>
								{editingItem ? "Saqlash" : "Kirish huquqini yaratish"}
							</Button>
						</Form.Item>
					</Form>
				</div>

				{/* Access Details */}
				<div>
					<Text strong className="block mb-4 text-ink">
						Kirish tafsilotlari
					</Text>
					<div className="border rounded-lg overflow-hidden">
						<table className="w-full text-sm">
							<thead>
								<tr className="bg-neutral-50 border-b">
									<th className="text-left py-3 px-4 font-medium text-ink-soft">Maydon</th>
									<th className="text-left py-3 px-4 font-medium text-ink-soft">Qiymat</th>
								</tr>
							</thead>
							<tbody>
								<tr className="border-b last:border-b-0">
									<td className="py-3 px-4 text-ink-soft">Ko'ruvchi tashkilot</td>
									<td className="py-3 px-4">{accessDetails.viewerName}</td>
								</tr>
								<tr className="border-b last:border-b-0">
									<td className="py-3 px-4 text-ink-soft">Tashkilot</td>
									<td className="py-3 px-4">{accessDetails.targetName}</td>
								</tr>
								<tr>
									<td className="py-3 px-4 text-ink-soft">Kirish turi</td>
									<td className="py-3 px-4">{accessDetails.accessType}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</Modal>
	);
}
